import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '../components/Card'
import { BarList, Donut, TrendChart } from '../components/charts'
import { EmptyState } from '../components/EmptyState'
import { Badge, ErrorBanner, Select } from '../components/forms'
import {
  ArrowRightIcon,
  BarsIcon,
  CheckIcon,
  CoinsIcon,
  DocumentIcon,
  PeopleIcon,
  StackIcon,
  WalletIcon,
} from '../components/icons'
import { Sparkline } from '../components/StatTile'
import { Permissions } from '../auth/permissions'
import { useSession } from '../auth/session'
import {
  periodLabels,
  previousLabels,
  useDashboard,
  type DashboardMovement,
  type DashboardPeriod,
  type DashboardShop,
  type DashboardSummary,
} from '../features/dashboard/api'
import { formatDate, formatDateTime, formatMoneyDocument, formatMoneyList } from '../lib/format'

/**
 * The owner's business overview (D-136, widened in D-138).
 *
 * What was sold, what moved, what came in and what is still owed — over today, the last
 * week or the last month, for every branch or one of them. Every panel appears only for
 * someone who may see the figures behind it, because the server sends only those: a
 * cashier opening this page sees the sales they made and nothing about what the company
 * is owed.
 */
export function Dashboard() {
  const user = useSession((s) => s.user)
  const can = useSession((s) => s.can)
  const [period, setPeriod] = useState<DashboardPeriod>('today')
  const [branch, setBranch] = useState('')

  const dashboard = useDashboard(period, branch || undefined)
  const d = dashboard.data

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-dpl-text sm:text-[28px] sm:leading-9">Business Overview</h2>
          <p className="mt-1 text-sm text-dpl-muted">
            {d ? `Live performance across ${d.locationId || d.scope !== 'All locations' ? d.scope : 'all shops'}` : `Good ${greeting()}, ${user?.name.split(' ')[0] ?? ''} — loading today’s figures…`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PeriodToggle period={period} onChange={setPeriod} />
          {d && d.locations.length > 1 && (
            <Select aria-label="Branch" value={branch} onChange={(e) => setBranch(e.target.value)} className="w-44">
              <option value="">All shops</option>
              {d.locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </Select>
          )}
          {d && (
            <div className="text-right text-xs text-dpl-muted">
              <p className="font-medium text-dpl-text">{formatDate(d.today)}</p>
              <p>{d.scope} · {rangeWords(d)}</p>
            </div>
          )}
        </div>
      </header>

      <ErrorBanner error={dashboard.error} />

      {!d ? (
        <div className="h-72 animate-pulse rounded-lg bg-dpl-surface" aria-busy="true" />
      ) : (
        <>
          <Headlines summary={d} can={can} />
          <Money summary={d} can={can} />

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {d.sales && d.shops.length > 0 && (
              <Card title={`Sales by shop · ${periodLabels[d.period]}`}>
                <BarList
                  label="Sales by shop"
                  bars={d.shops.map((s) => ({ key: s.locationId, label: s.name, value: s.sales }))}
                  format={formatMoneyList}
                />
                <p className="mt-4 flex items-center justify-between rounded-md bg-dpl-bg px-3 py-2.5 text-sm font-medium">
                  <span>Total sales</span>
                  <span className="tabular-nums">{formatMoneyList(d.sales.total)}</span>
                </p>
              </Card>
            )}
            {d.sales && (
              <Card title="Sales performance" className={d.shops.length > 0 ? '' : 'xl:col-span-2'}>
                <TrendChart
                  label="Sales over the period"
                  format={formatMoneyList}
                  target={d.sales.target == null ? null : d.sales.target / periodDays(d)}
                  points={d.sales.series.map((day) => ({
                    label: dayLabel(day.date, d.sales!.series.length),
                    value: day.total,
                    title: `${formatDate(day.date)}: ${formatMoneyDocument(day.total)} from ${day.invoices} ${day.invoices === 1 ? 'sale' : 'sales'}`,
                  }))}
                />
              </Card>
            )}
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {d.stock && <StockMovement summary={d} can={can} />}
            {d.debtors && <Receivables summary={d} can={can} />}
          </div>

          {d.shops.length > 0 && <ShopPerformance summary={d} />}

          <Card
            title="Recent activity"
            action={<CardLink to="/sales/register" label="View all activity" can={can(Permissions.Sales.ViewRegister)} />}
          >
            {d.activity.length === 0 ? (
              <EmptyState title="Nothing yet" hint="Sales, stock moving between locations and deliveries appear here as they happen." />
            ) : (
              <ul className="divide-y divide-dpl-border" aria-label="Recent activity">
                {d.activity.map((a) => (
                  <li key={`${a.kind}-${a.code}`} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${activityTint[a.kind]}`}>
                        {a.kind === 'Sale' ? <DocumentIcon className="h-4 w-4" /> : a.kind === 'Transfer' ? <ArrowRightIcon className="h-4 w-4" /> : <StackIcon className="h-4 w-4" />}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {a.link ? <Link to={a.link} className="text-dpl-primary hover:underline">{a.code}</Link> : a.code}
                          <span className="font-normal text-dpl-muted"> · {a.title}</span>
                        </p>
                        {a.detail && <p className="truncate text-xs text-dpl-muted">{a.detail}</p>}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      {a.amount !== undefined && a.amount !== null && <p className="text-sm tabular-nums">{formatMoneyDocument(a.amount)}</p>}
                      <p className="text-xs text-dpl-muted">{formatDateTime(a.at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </div>
  )
}

const activityTint: Record<string, string> = {
  Sale: 'bg-dpl-primary/10 text-dpl-primary',
  Transfer: 'bg-dpl-accent/15 text-[#8a5a06]',
  Delivery: 'bg-dpl-success/10 text-dpl-success',
}

/** The six figures the owner reads first. */
function Headlines({ summary: d, can }: { summary: DashboardSummary; can: (p: string) => boolean }) {
  const sales = d.sales
  const stock = d.stock
  const cash = d.cash

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {sales && (
        <Kpi
          icon={<CoinsIcon className="h-5 w-5" />}
          label={d.period === 'today' ? 'Total sales today' : `Total sales, ${periodLabels[d.period].toLowerCase()}`}
          value={formatMoneyList(sales.total)}
          change={change(sales.total, sales.previous)}
          caption={`${sales.invoices.toLocaleString('en-KE')} ${sales.invoices === 1 ? 'sale' : 'sales'}`}
          trend={sales.series.map((s) => s.total)}
          to={can(Permissions.Sales.ViewRegister) ? '/sales/register' : undefined}
        />
      )}
      {sales && (
        <Kpi
          icon={<BarsIcon className="h-5 w-5" />}
          label={`Sales ${previousLabels[d.period]}`}
          value={signed(change(sales.total, sales.previous))}
          caption={`${formatMoneyList(sales.total)} against ${formatMoneyList(sales.previous)}`}
          tone={toneOf(change(sales.total, sales.previous))}
        />
      )}
      {stock && (
        <Kpi
          icon={<StackIcon className="h-5 w-5" />}
          label="Stock sold"
          value={units(stock.sold.units)}
          change={change(stock.sold.units, stock.sold.previousUnits)}
          caption={stock.sold.value == null ? undefined : `${formatMoneyList(stock.sold.value)} at cost`}
          to={can(Permissions.Warehouse.ViewStock) ? '/warehouse' : undefined}
        />
      )}
      {stock && (
        <Kpi
          icon={<ArrowRightIcon className="h-5 w-5" />}
          label="Stock transferred"
          value={units(stock.transferredOut.units)}
          change={change(stock.transferredOut.units, stock.transferredOut.previousUnits)}
          caption={stock.transferredOut.value == null ? undefined : `${formatMoneyList(stock.transferredOut.value)} at cost`}
          to={can(Permissions.Warehouse.ViewStock) ? '/warehouse/orders' : undefined}
        />
      )}
      {d.debtors && (
        <Kpi
          icon={<PeopleIcon className="h-5 w-5" />}
          label="Debtors due"
          value={formatMoneyList(d.debtors.outstanding)}
          caption={`${d.debtors.customers} ${d.debtors.customers === 1 ? 'customer' : 'customers'} · ${formatMoneyList(d.debtors.overdue)} overdue`}
          tone={d.debtors.overdue > 0 ? 'danger' : 'neutral'}
          to={can(Permissions.Finance.RecordReceipt) || can(Permissions.Finance.ViewAccounts) ? '/finance/debtors' : undefined}
        />
      )}
      {cash?.collected != null && (
        <Kpi
          icon={<WalletIcon className="h-5 w-5" />}
          label="Cash collected"
          value={formatMoneyList(cash.collected)}
          change={change(cash.collected, cash.collectedPrevious ?? 0)}
          caption={sales && sales.total > 0 ? `${Math.round((cash.collected / sales.total) * 100)}% of sales` : undefined}
          tone="success"
          to={can(Permissions.Finance.RecordReceipt) ? '/finance/receipts' : undefined}
        />
      )}
    </div>
  )
}

/** What is owed on the period's invoices, and what the branches spent. */
function Money({ summary: d, can }: { summary: DashboardSummary; can: (p: string) => boolean }) {
  if (!d.invoices && !d.cash) return null

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {d.invoices && (
        <Tile
          icon={<DocumentIcon className="h-5 w-5" />}
          label="Invoices due"
          value={formatMoneyList(d.invoices.dueAmount)}
          caption={`${d.invoices.dueCount} ${d.invoices.dueCount === 1 ? 'invoice' : 'invoices'} raised, still unpaid`}
          to={can(Permissions.Finance.RecordReceipt) ? '/finance/debtors' : undefined}
        />
      )}
      {d.invoices && (
        <Tile
          icon={<CheckIcon className="h-5 w-5" />}
          label="Invoices paid"
          value={formatMoneyList(d.invoices.paidAmount)}
          caption={`${d.invoices.paidCount} ${d.invoices.paidCount === 1 ? 'invoice' : 'invoices'} settled`}
          tone="success"
        />
      )}
      {d.cash && (
        <Tile
          icon={<WalletIcon className="h-5 w-5" />}
          label="Petty cash expenses"
          value={formatMoneyList(d.cash.pettyCash)}
          caption={`${d.cash.pettyCashCount} ${d.cash.pettyCashCount === 1 ? 'voucher' : 'vouchers'}`}
          tone="accent"
          to={can(Permissions.Finance.PettyCashSpend) || can(Permissions.Finance.ApproveExpense) ? '/finance/expenses' : undefined}
        />
      )}
      {d.cash?.netPosition != null && (
        <Tile
          icon={<CoinsIcon className="h-5 w-5" />}
          label="Net cash position"
          value={formatMoneyList(d.cash.netPosition)}
          caption="Collected, after petty cash"
          tone={d.cash.netPosition < 0 ? 'danger' : 'success'}
        />
      )}
    </div>
  )
}

function StockMovement({ summary: d, can }: { summary: DashboardSummary; can: (p: string) => boolean }) {
  const stock = d.stock!

  return (
    <Card title={`Stock movement · ${periodLabels[d.period]}`}>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Movement label="Sold" movement={stock.sold} tone="success" />
        <Movement label="Transfers out" movement={stock.transferredOut} tone="accent" />
        <Movement label="Transfers in" movement={stock.transferredIn} tone="neutral" />
        <Movement label="Adjustments" movement={stock.adjusted} tone="danger" signed />
      </div>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 border-t border-dpl-border pt-3 text-sm">
        <FootLink
          to="/products?low=1"
          can={can(Permissions.Product.View)}
          text={stock.levelsSet === 0
            ? 'No reorder levels set yet'
            : `${stock.lowItems} ${stock.lowItems === 1 ? 'item' : 'items'} at or below reorder level${stock.outOfStock > 0 ? `, ${stock.outOfStock} out of stock` : ''}`}
        />
        {d.deliveries && (
          <FootLink
            to="/logistics"
            can={can(Permissions.Delivery.ViewBoard)}
            text={`${d.deliveries.active} ${d.deliveries.active === 1 ? 'delivery' : 'deliveries'} active${d.deliveries.overdue > 0 ? `, ${d.deliveries.overdue} overdue` : ''}`}
          />
        )}
      </div>
    </Card>
  )
}

function Receivables({ summary: d, can }: { summary: DashboardSummary; can: (p: string) => boolean }) {
  const debtors = d.debtors!
  const cash = d.cash
  const rate = cash?.collected != null && d.sales && d.sales.total > 0 ? (cash.collected / d.sales.total) * 100 : null
  const target = cash?.collectionTarget ?? null
  const seeDebtors = can(Permissions.Finance.RecordReceipt) || can(Permissions.Finance.ViewAccounts)

  return (
    <Card
      title="Receivables & collections"
      action={<CardLink to="/finance/debtors" label="View debtors" can={seeDebtors} />}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Movement label="Debtors due" value={formatMoneyList(debtors.outstanding)} caption={`${debtors.customers} ${debtors.customers === 1 ? 'customer' : 'customers'}`} tone="neutral" />
        <Movement
          label="Overdue"
          value={formatMoneyList(debtors.overdue)}
          caption={`${debtors.overdueCustomers} ${debtors.overdueCustomers === 1 ? 'customer' : 'customers'}`}
          tone="danger"
          // Only where there is something to open, and only for someone allowed to see the ledger.
          to={debtors.overdueCustomers > 0 && seeDebtors ? '/finance/debtors?overdue=1' : undefined}
        />
        {cash?.collected != null && <Movement label="Collected" value={formatMoneyList(cash.collected)} caption={rangeWords(d)} tone="success" />}
        {rate !== null && (
          <div className="flex items-center rounded-lg border border-dpl-border p-3">
            <Donut
              percent={rate}
              target={target}
              tone={target === null || rate >= target ? 'success' : 'danger'}
              label="Collection rate"
              caption={target === null ? 'No target set' : `Target ${Math.round(target)}%`}
            />
          </div>
        )}
      </div>
    </Card>
  )
}

/** Every branch's period side by side, against what it was asked for. */
function ShopPerformance({ summary: d }: { summary: DashboardSummary }) {
  const shops = d.shops
  const totals = {
    sales: sum(shops, (s) => s.sales),
    target: shops.some((s) => s.target != null) ? sum(shops, (s) => s.target ?? 0) : null,
    units: sum(shops, (s) => s.units),
    collections: sum(shops, (s) => s.collections),
    expenses: sum(shops, (s) => s.expenses),
  }

  return (
    <Card title={`Shop performance · ${periodLabels[d.period]}`}>
      <div className="-mx-4 overflow-x-auto sm:-mx-5">
        <table className="w-full min-w-[54rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-dpl-border">
              <th scope="col" className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-dpl-muted sm:px-5">Shop</th>
              <Head>Sales</Head>
              <Head>Target</Head>
              <Head>Achievement</Head>
              <Head>Units sold</Head>
              <Head>Collections</Head>
              <Head>Expenses</Head>
              <Head>Net sales</Head>
              <th scope="col" className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-dpl-muted sm:px-5">Status</th>
            </tr>
          </thead>
          <tbody>
            {shops.map((shop) => (
              <tr key={shop.locationId} className="border-b border-dpl-border/70">
                <td className="whitespace-nowrap px-3 py-3.5 font-medium sm:px-5">{shop.name}</td>
                <Cell>{formatMoneyList(shop.sales)}</Cell>
                <Cell>{shop.target == null ? '—' : formatMoneyList(shop.target)}</Cell>
                <Cell>{achievement(shop) === null ? '—' : `${Math.round(achievement(shop)!)}%`}</Cell>
                <Cell>{units(shop.units)}</Cell>
                <Cell>{formatMoneyList(shop.collections)}</Cell>
                <Cell>{formatMoneyList(shop.expenses)}</Cell>
                <Cell>{formatMoneyList(shop.sales - shop.expenses)}</Cell>
                <td className="whitespace-nowrap px-3 py-3.5 sm:px-5"><Status shop={shop} /></td>
              </tr>
            ))}
            <tr className="bg-dpl-bg/60 font-semibold">
              <td className="whitespace-nowrap px-3 py-3.5 sm:px-5">Total</td>
              <Cell>{formatMoneyList(totals.sales)}</Cell>
              <Cell>{totals.target === null ? '—' : formatMoneyList(totals.target)}</Cell>
              <Cell>{totals.target ? `${Math.round((totals.sales / totals.target) * 100)}%` : '—'}</Cell>
              <Cell>{units(totals.units)}</Cell>
              <Cell>{formatMoneyList(totals.collections)}</Cell>
              <Cell>{formatMoneyList(totals.expenses)}</Cell>
              <Cell>{formatMoneyList(totals.sales - totals.expenses)}</Cell>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-dpl-muted">
        Net sales are what the shop sold less what it spent. A shop is on track at 90% of its target or better; a shop with no target set is
        not measured. Targets are set in Settings.
      </p>
    </Card>
  )
}

/** At or above nine tenths of the target. Below that an owner wants to be told, not reassured. */
const ON_TRACK = 90

function Status({ shop }: { shop: DashboardShop }) {
  const reached = achievement(shop)
  if (reached === null) return <span className="text-xs text-dpl-muted">No target set</span>
  return <Badge tone={reached >= ON_TRACK ? 'success' : 'warning'}>{reached >= ON_TRACK ? 'On track' : 'Below target'}</Badge>
}

function achievement(shop: DashboardShop): number | null {
  return shop.target == null || shop.target === 0 ? null : (shop.sales / shop.target) * 100
}

const sum = <T,>(rows: T[], of: (row: T) => number) => rows.reduce((total, row) => total + of(row), 0)

function Head({ children }: { children: ReactNode }) {
  return <th scope="col" className="whitespace-nowrap px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-dpl-muted sm:px-5">{children}</th>
}

function Cell({ children }: { children: ReactNode }) {
  return <td className="whitespace-nowrap px-3 py-3.5 text-right tabular-nums sm:px-5">{children}</td>
}

// --- the pieces a panel is made of ------------------------------------------

type Tone = 'neutral' | 'success' | 'danger' | 'accent'

const tints: Record<Tone, string> = {
  neutral: 'bg-dpl-primary/10 text-dpl-primary',
  success: 'bg-dpl-success/10 text-dpl-success',
  danger: 'bg-dpl-danger/10 text-dpl-danger',
  accent: 'bg-dpl-accent/15 text-[#8a5a06]',
}

/**
 * One headline figure: what it is, the number, how it moved, and the line that says what
 * the number is made of. Wrapped in a link wherever there is a list behind it — a number
 * nobody can click is a number nobody can check.
 */
function Kpi({
  icon,
  label,
  value,
  change: moved,
  caption,
  trend,
  tone = 'neutral',
  to,
}: {
  icon: ReactNode
  label: string
  value: string
  change?: number | null
  caption?: string
  trend?: number[]
  tone?: Tone
  to?: string
}) {
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tints[tone]}`}>{icon}</span>
        {trend && trend.length > 1 && (
          // Coloured by the same direction as the arrow below it: a green line over a fall
          // is the sort of contradiction a reader resolves in the chart's favour.
          <Sparkline values={trend} className={`h-8 w-16 ${moved == null || moved === 0 ? 'text-dpl-muted' : moved > 0 ? 'text-dpl-success' : 'text-dpl-danger'}`} />
        )}
      </div>
      <p className="mt-3 text-sm text-dpl-muted">{label}</p>
      <p className="mt-0.5 truncate text-xl font-bold tabular-nums tracking-tight text-dpl-text 2xl:text-2xl">{value}</p>
      {moved !== undefined && <Change value={moved} />}
      {caption && <p className="mt-0.5 text-xs text-dpl-muted">{caption}</p>}
    </>
  )

  const shell = 'rounded-lg border border-dpl-border bg-dpl-surface p-4 shadow-sm'

  return to ? (
    <Link to={to} className={`${shell} block transition hover:border-dpl-primary/30 hover:shadow focus:outline-none focus:ring-2 focus:ring-dpl-primary/40`}>
      {body}
    </Link>
  ) : (
    <div className={shell}>{body}</div>
  )
}

/** The smaller card of the second row: an icon beside the figure, no trend. */
function Tile({ icon, label, value, caption, tone = 'neutral', to }: { icon: ReactNode; label: string; value: string; caption: string; tone?: Tone; to?: string }) {
  const body = (
    <div className="flex items-center gap-3.5">
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${tints[tone]}`}>{icon}</span>
      <div className="min-w-0">
        <p className="text-sm text-dpl-muted">{label}</p>
        <p className="truncate text-lg font-bold tabular-nums text-dpl-text">{value}</p>
        <p className="truncate text-xs text-dpl-muted">{caption}</p>
      </div>
    </div>
  )

  const shell = 'rounded-lg border border-dpl-border bg-dpl-surface p-4 shadow-sm'

  return to ? (
    <Link to={to} className={`${shell} block transition hover:border-dpl-primary/30 hover:shadow focus:outline-none focus:ring-2 focus:ring-dpl-primary/40`}>
      {body}
    </Link>
  ) : (
    <div className={shell}>{body}</div>
  )
}

/** A quantity that moved, or any figure with a caption, in a tinted box. */
function Movement({
  label,
  movement,
  value,
  caption,
  tone,
  signed: withSign = false,
  to,
}: {
  label: string
  movement?: DashboardMovement
  value?: string
  caption?: string
  tone: Tone
  signed?: boolean
  /** Where the figure leads. A tile with one becomes a link; without, it stays a plain tile. */
  to?: string
}) {
  const shown = movement ? `${withSign && movement.units > 0 ? '+' : ''}${units(movement.units)}` : value!
  const under = movement
    ? movement.value == null
      ? undefined
      : `${formatMoneyList(Math.abs(movement.value))} at cost`
    : caption

  const body = (
    <>
      <p className="text-xs text-dpl-muted">{label}</p>
      <p className={`mt-1 truncate text-lg font-bold tabular-nums ${tone === 'danger' ? 'text-dpl-danger' : tone === 'success' ? 'text-dpl-success' : 'text-dpl-text'}`}>{shown}</p>
      {under && <p className="truncate text-xs text-dpl-muted">{under}</p>}
      {movement && <Change value={change(movement.units, movement.previousUnits)} />}
    </>
  )

  // The whole tile is the target, not the number inside it: it is what the eye lands on, and on a
  // phone it is the only part big enough to hit.
  return to ? (
    <Link to={to} className="block rounded-lg border border-dpl-border p-3 transition hover:border-dpl-primary hover:bg-dpl-bg">
      {body}
      <p className="mt-0.5 text-xs font-medium text-dpl-primary">See who →</p>
    </Link>
  ) : (
    <div className="rounded-lg border border-dpl-border p-3">{body}</div>
  )
}

/** The arrow and the percentage, coloured by direction. */
function Change({ value }: { value: number | null }) {
  if (value === null) return <p className="mt-0.5 text-xs text-dpl-muted">Nothing to compare with</p>
  const rounded = Math.round(value)
  return (
    <p className={`mt-0.5 text-xs font-medium ${rounded > 0 ? 'text-dpl-success' : rounded < 0 ? 'text-dpl-danger' : 'text-dpl-muted'}`}>
      {rounded > 0 ? '▲' : rounded < 0 ? '▼' : '→'} {signed(value)}
    </p>
  )
}

function FootLink({ to, can, text }: { to: string; can: boolean; text: string }) {
  return can ? (
    <Link to={to} className="text-dpl-primary hover:underline">{text}</Link>
  ) : (
    <span className="text-dpl-muted">{text}</span>
  )
}

function CardLink({ to, label, can }: { to: string; label: string; can: boolean }) {
  return can ? <Link to={to} className="text-sm font-medium text-dpl-primary hover:underline">{label} →</Link> : null
}

function PeriodToggle({ period, onChange }: { period: DashboardPeriod; onChange: (p: DashboardPeriod) => void }) {
  return (
    <div className="flex rounded-lg border border-dpl-border bg-dpl-surface p-1" role="group" aria-label="Period">
      {(Object.keys(periodLabels) as DashboardPeriod[]).map((key) => (
        <button
          key={key}
          type="button"
          aria-pressed={key === period}
          onClick={() => onChange(key)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${key === period ? 'bg-dpl-primary text-white' : 'text-dpl-text hover:bg-dpl-bg'}`}
        >
          {periodLabels[key]}
        </button>
      ))}
    </div>
  )
}

// --- figures into words -----------------------------------------------------

/**
 * The move as a percentage, or null where there is nothing to compare with. Growth from
 * nothing is not "infinite per cent"; it is a figure that has no percentage.
 */
function change(now: number, before: number): number | null {
  if (before === 0) return now === 0 ? 0 : null
  return ((now - before) / before) * 100
}

function signed(value: number | null): string {
  if (value === null) return '—'
  const rounded = Math.round(value)
  return `${rounded > 0 ? '+' : ''}${rounded}%`
}

function toneOf(value: number | null): Tone {
  return value === null ? 'neutral' : value > 0 ? 'success' : value < 0 ? 'danger' : 'neutral'
}

const units = (quantity: number) => `${Number(quantity.toFixed(2)).toLocaleString('en-KE')} ${Math.abs(quantity) === 1 ? 'unit' : 'units'}`

/** How many business days the period covers — what a day's target is a share of. */
function periodDays(d: DashboardSummary): number {
  const from = new Date(`${d.from}T12:00:00Z`).getTime()
  const to = new Date(`${d.to}T12:00:00Z`).getTime()
  return Math.max(1, Math.round((to - from) / 86_400_000) + 1)
}

/** "Today", or the span the period covers. */
function rangeWords(d: DashboardSummary): string {
  return d.period === 'today' ? 'Today' : `${formatDate(d.from)} – ${formatDate(d.to)}`
}

/** A weekday on a short series, the day of the month on a long one. */
function dayLabel(date: string, points: number): string {
  const day = new Date(`${date}T12:00:00`)
  return points > 10
    ? day.toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })
    : day.toLocaleDateString('en-KE', { weekday: 'short' })
}

function greeting() {
  const hour = Number(new Intl.DateTimeFormat('en-KE', { hour: 'numeric', hourCycle: 'h23', timeZone: 'Africa/Nairobi' }).format(new Date()))
  return hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening'
}
